import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import { isBefore } from 'date-fns'
import type { PopupLocalStorageData } from 'src/constant'
import type { ParamDataT } from 'src/dto/common/ReqParams'
import type { StrapiDataResponse } from 'src/types/common/api'

import noticeApi from '@/api/notice.api'
import type { Academy, ReqParams, User } from '@/dto'
import { AcademyNotice, Banner, NoticeColl, ParentsAppNotice } from '@/dto'
import ExtendedMap from '@/models/ExtendedMap'
import commonRepo from '@/repository/common.repo.ts'
import { localStorageService } from '@/service/storage.service'

import { makeAdapter } from './Adapter'
import type Controller from './Controller'

type FetchPopupProps = {
  teacherId: User['teacherId']
  academyStatus: Academy['status']
  academyMetaData: Academy['metadata']
  academyAuthority: Academy['authority']
  brandStatus: 'B2B' | 'B2G'
}

export class NoticeController implements Controller {
  adapter = {
    // 공지 및 업데이트
    academyNotice: makeAdapter({
      from({ id, attributes }: StrapiDataResponse<res.AcademyNotice>): AcademyNotice {
        return new AcademyNotice({
          id,
          ...attributes,
          content: domainProvider.replaceAllMathFlatDomain(attributes.content),
        })
      },
    }),
    // 학부모 앱 공지
    parentsApp: makeAdapter({
      from(data: res.ParentsAppNotice): ParentsAppNotice {
        return new ParentsAppNotice(data)
      },
      to(parentsAppNotice: ParentsAppNotice): req.ParentsAppNotice {
        const { title, type, content, marked } = parentsAppNotice
        return {
          title,
          type,
          content: domainProvider.replaceAllMathFlatDomain(content),
          marked,
        }
      },
      toId(parentsAppNotice: ParentsAppNotice): req.ParentsAppNoticeId {
        return parentsAppNotice.id
      },
    }),
    banner: makeAdapter({
      from(data: StrapiDataResponse<res.Banner>, user: User): Banner | void {
        const bannerData = data?.attributes
        const _bannerData = bannerData
          ? {
              ...bannerData,
              tooltipImageUrl: domainProvider.replaceMathFlatDomain(
                bannerData?.tooltipImageUrl || '',
              ),
              longImageUrl: domainProvider.replaceMathFlatDomain(bannerData?.longImageUrl || ''),
              redirectUrl: domainProvider.replaceMathFlatDomain(bannerData?.redirectUrl || ''),
            }
          : bannerData
        const id = String(data?.id)

        if (!_bannerData && commonRepo.isSchoolflat) {
          return
        }
        return new Banner({ user, data: _bannerData, id })
      },
    }),
  }
  // 로컬호스트에서 다시보지 않음에 체크 된 팝업의 id를 가져온다.
  private excludedPopupIdsFromLocalStorage(storageKey: string) {
    const storedPopups = JSON.parse(
      localStorage.getItem(storageKey) ?? '[]',
    ) as PopupLocalStorageData[]

    // 1. localStorage에서 strapi fetch에서 제외할 팝업들을 구한다.
    const excludedPopups = storedPopups.filter(({ expiredDate }) => {
      return expiredDate === 'infinite' || isBefore(new Date(), new Date(expiredDate))
    })

    // 2. 제외된 팝업은 expiredDate가 지난 것들이 삭제된 최신의 값과 같으므로, 겸사겸사 localStorage값을 리뉴얼 시켜준다.
    localStorage.setItem(storageKey, JSON.stringify(excludedPopups))

    // 3. id만 추출
    return excludedPopups.map(({ id }) => id)
  }
  private makeTargetFilter({
    academyStatus,
    popupTargetingGroup,
    academyAuthority,
    excludedIds,
  }: {
    academyStatus: FetchPopupProps['academyStatus']
    popupTargetingGroup: FetchPopupProps['academyMetaData']['popupTargetingGroup']
    academyAuthority: FetchPopupProps['academyAuthority']
    excludedIds: number[]
  }) {
    const academyGroup =
      popupTargetingGroup && popupTargetingGroup !== '' ? popupTargetingGroup.split(',') : []

    // group에 대괄호를 양옆에 넣는 이유: strapi에서 contains 필터를 사용 시, 문자열로 includes 여부를 판단하기 때문에 중복된 string의 값이 있는 그룹의 경우 필터링이 제대로 되지 않기 때문.
    // 따라서 strapi에서도 include와 exclude의 필드에 각 그룹을 대괄호로 감싸서 값을 넣어주어야함.
    const includeFilterList = academyGroup.map((group) => ({
      include: { $contains: `[${group}]` }, // ex. { $contains: [초등] }
    }))

    const excludeFilterList = academyGroup.map((group) => ({
      exclude: { $notContains: `[${group}]` },
    }))

    const filters: { $and: any } = {
      $and: [
        { id: { $notIn: excludedIds } },
        {
          target: {
            [academyStatus]: true,
          },
        },
        { $or: [{ authority: { [academyAuthority]: true } }, { authority: { $null: true } }] },
        { $or: [...includeFilterList, { include: { $null: true } }] },
      ],
    }

    if (excludeFilterList.length > 0) {
      filters.$and.push({
        $or: [{ $and: [...excludeFilterList] }, { exclude: { $null: true } }],
      })
    }
    return filters
  }
  private makeNoticeBoardTargetFilter({
    brandStatus,
    academyStatus,
    popupTargetingGroup,
    academyAuthority,
  }: {
    brandStatus: FetchPopupProps['brandStatus']
    academyStatus: FetchPopupProps['academyStatus']
    popupTargetingGroup: FetchPopupProps['academyMetaData']['popupTargetingGroup']
    academyAuthority: FetchPopupProps['academyAuthority']
  }) {
    const academyGroup =
      popupTargetingGroup && popupTargetingGroup !== '' ? popupTargetingGroup.split(',') : []

    // group에 대괄호를 양옆에 넣는 이유: strapi에서 contains 필터를 사용 시, 문자열로 includes 여부를 판단하기 때문에 중복된 string의 값이 있는 그룹의 경우 필터링이 제대로 되지 않기 때문.
    // 따라서 strapi에서도 include와 exclude의 필드에 각 그룹을 대괄호로 감싸서 값을 넣어주어야함.
    const includeFilterList = academyGroup.map((group) => ({
      include: { $contains: `[${group}]` }, // ex. { $contains: [초등] }
    }))

    const excludeFilterList = academyGroup.map((group) => ({
      exclude: { $notContains: `[${group}]` },
    }))

    const filters: { $and: any } = {
      $and: [
        {
          target: {
            [academyStatus]: true,
          },
          target2: {
            [brandStatus]: true,
          },
        },
        { $or: [{ authority: { [academyAuthority]: true } }, { authority: { $null: true } }] },
        { $or: [...includeFilterList, { include: { $null: true } }] },
      ],
    }

    if (excludeFilterList.length > 0) {
      filters.$and.push({
        $or: [{ $and: [...excludeFilterList] }, { exclude: { $null: true } }],
      })
    }
    return filters
  }

  // 공지 및 업데이트
  async fetchAcademyNotices(reqParams: ReqParams<ParamDataT>) {
    const { data, meta } = await noticeApi.getAcademies({
      sort: ['createdAt:desc'],
      pagination: { page: reqParams.params.page, pageSize: reqParams.params.size },
    })
    const academyNoticeArr = data.map(this.adapter.academyNotice.from)
    const academyNoticeMap = ExtendedMap.fromArr(academyNoticeArr, 'id')

    return {
      academyNoticeMap: new NoticeColl(academyNoticeMap),
      last: meta.pagination?.page === meta.pagination?.pageCount,
    }
  }

  // 신규 매쓰플랫 알림판
  async fetchNoticeBoard({
    academyStatus,
    academyMetaData,
    academyAuthority,
    brandStatus,
  }: FetchPopupProps) {
    const filters = this.makeNoticeBoardTargetFilter({
      brandStatus,
      academyStatus,
      popupTargetingGroup: academyMetaData.popupTargetingGroup,
      academyAuthority,
    })

    const {
      data: { data },
    } = await noticeApi.getNoticeBoard({
      sort: ['publishedAt:desc'],
      filters,
    })

    return data.map(({ ...rest }) => ({
      ...rest,
      attributes: {
        ...rest.attributes,
        content: domainProvider.replaceAllMathFlatDomain(rest.attributes.content),
        media: {
          ...rest.attributes.media,
          data:
            rest.attributes.media.data?.map((item) => ({
              ...item,
              attributes: {
                ...item.attributes,
                url: domainProvider.replaceAllMathFlatDomain(item.attributes.url),
              },
            })) || null,
        },
      },
    }))
  }

  // 팝업 - 온보딩
  /** deprecated 인듯..? */
  async fetchOnboardingPopup(teacherId: User['teacherId']) {
    const STORAGE_KEY = `ONBOARDING_POPUP_READ_${teacherId}` as const
    const excludedNames = localStorageService.getItem(STORAGE_KEY) ?? []

    const filters = { name: { $notIn: excludedNames } }

    const {
      data: { data },
    } = await noticeApi.getOnboardingPopup({
      sort: ['priority:desc'],
      filters,
    })

    return data
  }

  // 무료체험 툴팁 배너
  async fetchBanner(user: User, isSchoolflat: boolean, excludeIds?: Banner['id'][]) {
    const filters = {
      $and: [
        {
          $not: { id: { $in: { ...excludeIds } } },
        },
        {
          expiredAt: { $gt: new Date().toISOString() },
        },
        { academyTypeTarget: isSchoolflat ? { B2G: true } : { B2B: true } },
      ],
    }
    const {
      data: { data },
    } = await noticeApi.getBanner({
      sort: ['priority:desc'],
      filters,
      pagination: {
        limit: 1,
      },
    })

    return this.adapter.banner.from(data[0], user)
  }

  async fetchUpdatedContents() {
    const {
      data: { data },
    } = await noticeApi.getUpdatedContents()
    return data
  }

  // 학부모 앱 공지
  async fetchParentsAppNotices(pageQueryString: string) {
    const {
      data: {
        data: { content, last },
      },
    } = await noticeApi.getParentsAppNotices(pageQueryString)
    const parentsAppNotices = content.map(this.adapter.parentsApp.from)
    const parentsAppNoticeMap = ExtendedMap.fromArr(parentsAppNotices, 'id')
    return { parentsAppNotices: new NoticeColl(parentsAppNoticeMap), last }
  }

  async createParentsAppNotice(parentsAppNotice: ParentsAppNotice) {
    await noticeApi.postParentsAppNotice(this.adapter.parentsApp.to(parentsAppNotice))
  }

  async changeParentsAppNotice(parentsAppNotice: ParentsAppNotice) {
    await noticeApi.putParentsAppNotice(
      this.adapter.parentsApp.toId(parentsAppNotice),
      this.adapter.parentsApp.to(parentsAppNotice),
    )
  }

  async deleteParentsAppNotice(parentsAppNoticeIds: number[]) {
    await noticeApi.deleteParentsAppNotice(parentsAppNoticeIds)
  }

  async fetchServerMaintenanceNotice() {
    const res = await noticeApi.getServerMaintenanceNotice()
    return {
      ...res.data.data.attributes,
      startDate: new Date(new Date(res.data.data.attributes.startDate).getTime()),
      endDate: new Date(new Date(res.data.data.attributes.endDate).getTime()),
    }
  }
}

export const noticeController = new NoticeController()
